/**
 * AdminRoute — UX-only gate for admin pages (M58 Step 10a).
 *
 * Renders children when the authenticated user's id is in the
 * `VITE_APP_ADMIN_USER_IDS` allowlist (comma-separated UUIDs).
 * Otherwise redirects to `/`.
 *
 * Per ADR-037 §Admin auth, the security boundary is the server-side
 * check inside the JWT-gated `generate-beta-invite` Edge Function.
 * This wrapper is defense-in-depth / UX (hide the admin page from
 * non-admins so they don't see an unstyled error). A non-admin who
 * fakes their UID locally would still be rejected by the Edge Function.
 *
 * AdminRoute is mounted **inside** AuthGuard (see App.tsx), so by the
 * time this component renders the auth status is `'authenticated'`
 * and `user` is non-null. The defensive null check is kept as a
 * narrowing guard, not a runtime branch we expect to hit.
 */
import { Navigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth-store';

interface AdminRouteProps {
  children: React.ReactNode;
}

function parseAdminIds(raw: string | undefined): readonly string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

export function AdminRoute({ children }: AdminRouteProps) {
  const user = useAuthStore((s) => s.user);

  const adminIdsRaw = import.meta.env.VITE_APP_ADMIN_USER_IDS as string | undefined;
  const adminIds = parseAdminIds(adminIdsRaw);

  if (!user || !adminIds.includes(user.id)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
